import React from "react";
import { Select, Tag, Tooltip } from "antd";
import { Currency } from "@/Contracts";
import { useCurrencyConfig } from "@/Hooks/useCurrencyConfig";

interface OpportunityCurrencyDropdownProps {
	value?: Currency | null;
	onChange?: (currency: Currency | null) => void;
	disabled?: boolean;
	lockedReason?: string;
	placeholder?: string;
}

const PRIMARY_OPTION_VALUE = "__primary__";

const OpportunityCurrencyDropdown: React.FC<OpportunityCurrencyDropdownProps> = ({
	value = null,
	onChange,
	disabled = false,
	lockedReason,
	placeholder = "Select currency"
}) => {
	const { config, loading } = useCurrencyConfig();

	// Backend may return either a string code or a {code, display_name} object;
	// coerce to string so AntD's Select doesn't try to render an object child.
	const valueCode: string | null =
		value && typeof value === "object" && "code" in value
			? (value as { code: string }).code
			: (value as string | null);

	const selectValue =
		valueCode === null || valueCode === undefined
			? config
				? PRIMARY_OPTION_VALUE
				: undefined
			: valueCode;

	const handleChange = (next: string) => {
		if (!onChange) return;
		if (next === PRIMARY_OPTION_VALUE) {
			onChange(null);
		} else {
			onChange(next as Currency);
		}
	};

	const primaryCode = config?.primaryCurrency.code ?? "";
	const options = config
		? [
				{
					value: PRIMARY_OPTION_VALUE,
					label: (
						<span className="inline-flex items-center gap-2">
							<span>{primaryCode}</span>
							<Tag color="blue" style={{ margin: 0 }}>
								Company Currency
							</Tag>
						</span>
					)
				},
				...config.currencyConfigs.map((c) => ({
					value: c.currency.code,
					label: (
						<span>
							{c.currency.code} (
							<strong>1 {primaryCode}</strong> ={" "}
							<strong>
								{c.exchangeRate} {c.currency.code}
							</strong>
							)
						</span>
					)
				}))
		  ]
		: [];

	const select = (
		<Select
			value={selectValue}
			onChange={handleChange}
			disabled={disabled || loading || !config}
			loading={loading}
			placeholder={placeholder}
			style={{ width: "100%" }}
			aria-label="Currency"
			virtual={false}
			options={options}
		/>
	);

	if (disabled && lockedReason) {
		return <Tooltip title={lockedReason}>{select}</Tooltip>;
	}

	return select;
};

export default OpportunityCurrencyDropdown;
